import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;

/**
 * Feel free to modify this type for this project (only) here
 * For example, if you add a whole new block of text that the jslib does not have a spot for already
 *
 * This is not necessary if you are just overriding text for a pre-existing message id
 */
type ThisProjectMessages = {
    softwareTypes: {
        online: string;
        desktop: string;
        tax: string;
    };
    errorId: {
        invalidMajor: string;
        invalidQuickBooksDesktopVersion: string;
        invalidSoftwareType: string;
        invalidConfirmEmail: string;
        nonMatchEmail: string;
    };
    step: {
        personalInfo: {
            subtitleUS: string;
        };
        success: {
            createAccount: string;
            bestWishesUS: string;
            intuitTeamUS: string;
            online: {
                title: string;
                subtitle: string;
                accessLink: string;
                bestWishes: string;
                bestWishesUS: string;
                subtitleUS: string;
                createAccountUS: string;
                createAccountCA: string;
                downloadLinkUS: string;
                downloadLinkCA: string;
            };
            desktop: {
                title: string;
                subtitle: string;
                accessLink: string;
                bestWishes: string;
                bestWishesUS: string;
                subtitleUS: string;
                accessLinkUS: string;
                createAccountUS: string;
                createAccountCA: string;
                downloadLinkUS: string;
                downloadLinkCA: string;
            };
            tax: {
                title: string;
                titleUS: string;
                subtitle: string;
                subtitleUS: string;
                accessLink: string;
                licenseNumber: string;
                productNumber: string;
                downloadLinkInstructionsCA: string;
                bestWishes: string;
                bestWishesUS: string;
                createAccountUS: string;
                createAccountCA: string;
                downloadLinkUS: string;
                downloadLinkCA: string;
            };
        };
        docUpload: {
            footer: string;
        };
    };
    birthDatePlaceholder: string;
    confirmEmailAddress: string;
    other: string;
    accounting: string;
    bookkeeping: string;
    business: string;
    entrepreneurship: string;
    finance: string;
    major: string;
    quickbooksDesktopVersion: string;
    returnToIntuit: string;
    intuitEducationTeamUS: string;
    intuitEducationTeam: string;
    contactIntuit: string;
    contactIntuitUS: string;
    infoShared: string;
};

/**
 * Define the actual language strings here, as needed.
 * This object should be sparse, so you inherit language from the library. Only override what you need to.
 *
 * The complicated type ensures your object adheres to the library's type(s), as well as any additional type you've
 * created in ThisProjectMessages.
 */
export const messagesEN: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    birthDate: 'Birth Date',
    birthDatePlaceholder: 'yyyy/mm/dd',
    emailAddress: 'Email Address',
    confirmEmailAddress: 'Confirm Email Address',
    schoolName: 'School Name',
    verifyMyStudentStatus: 'Verify and continue',
    other: 'Other',
    accounting: 'Accounting',
    bookkeeping: 'Bookkeeping',
    business: 'Business',
    entrepreneurship: 'Entrepreneurship',
    finance: 'Finance',
    major: 'Major/Concentration',
    quickbooksDesktopVersion: 'QuickBooks Desktop Version',
    returnToIntuit: 'Return to Intuit',
    intuitEducationTeamUS: 'Thanks!<br>The Intuit Team',
    intuitEducationTeam: '- The Intuit Team',
    contactIntuitUS: 'If that link doesn\'t work, <a class="sid-link" href="mailto:education@intuit.com">Education Support</a> can help.',
    contactIntuit: 'If that link doesn\'t work, <a class="sid-link" href="mailto:iepcanada@intuit.com">Education Support</a> can help.',
    softwareTypes: {
        online: 'QuickBooks Online',
        desktop: 'QuickBooks Desktop',
        tax: 'ProFile',
    },
    error: 'An error occurred',
    errorId: {
        invalidMajor: 'Invalid major',
        invalidQuickBooksDesktopVersion: 'Invalid QuickBooks Desktop version',
        invalidSoftwareType: 'Please return to Intuit.',
        invalidConfirmEmail: 'Invalid confirm email',
        nonMatchEmail: 'Emails do not match',
    },
    step: {
        personalInfo: {
            title: 'Student Registration',
            subtitle: 'Students at accredited academic institutions are eligible for a one-year student registration. College, University, and Trade-School students please complete the form below to confirm eligibility and unlock your free license for {software}. High School students, please have your teacher complete the educator registration.',
            subtitleUS: 'Students at accredited academic institutions are eligible for a one-year student registration. College, University, and Trade-School students please complete the form below to confirm eligibility and unlock your free license for ProConnect. High School students, please have your teacher complete the educator registration.',
        },
        docUpload: {
            title: 'Verification documents',
            subtitle: 'We apologize, we couldn’t verify your student status.<br />Documents will be reviewed in {estimatedWaitTime} by staff at SheerID, a trusted partner.',
            submitButtonLabel: 'Send documents',
            uploadInstructions: 'Upload your school-issued document that shows:',
            footer: 'Make sure the document image is clear, readable, and displays your full legal name as enrolled at your school. <strong>Do not include any confidential personal info such as SSN or banking details.</strong> Black these out by hand before you scan the form or use an editing feature on your computer.',
            howDoesVerifyingWorkDetails: `Document review is handled by SheerID, a trusted partner who specializes in verifying students.
            <br/><br/>
            Documents are used only for verifying that your student status is current and will be permanently deleted after review is finalized. They will not be shared with {companyName}`,
        },
        pending: {
            titleReview: 'Document in review',
            subtitle: 'We received your document! We\'re reviewing now to verify your enrollment status.',
            subtitle2: 'This should take less than 20 minutes. This page will update with your verification status and we\'ll also send you an email with your status, so you don\'t have to stay here to get that information.',
            subtitle3: 'If you don\'t receive it, check your spam folder for mail from verify@sheerid.com.',
        },
        success: {
            title: 'Welcome to Intuit Education!',
            createAccount: '<a class="sid-link" href="{accessLink}">Create your account</a>',
            bestWishesUS: "If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>.",
            intuitTeamUS: 'Thanks!<br>The Intuit Team',
            online: {
                title: 'Welcome to Intuit QuickBooks',
                subtitle: 'We verified your enrollment status. Here is a 1-year trial of QuickBooks Online Plus!',
                bestWishes: 'Best wishes on your learning journey with QuickBooks!',
                bestWishesUS: "If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>.",
                subtitleUS: 'We have successfully verified your student credentials and your free 1-year trial of QuickBooks Online Plus is ready!',
                createAccountUS: "<a class='sid-link' href='{accessLink}'>Create your QuickBooks Online Plus account.</a> Please work with your instructor to either follow curriculum or their instructions for account setup.",
                createAccountCA: '<a class="sid-link" href="{accessLink}">Create your account</a>',
                downloadLinkUS: 'https://signup.quickbooks.intuit.com/?locale=en-us&offerType=free&offerId=20016081&voucherCode={rewardCode}',
                downloadLinkCA: 'https://signup.quickbooks.intuit.com/?locale=en-ca&offerType=free&bc=OBI-LL1&cpnid=web&offerId=20022176&voucherCode={rewardCode}',
            },
            desktop: {
                title: 'Welcome to Intuit QuickBooks',
                subtitle: 'We verified your enrollment status. Here is a 5-month trial of QuickBooks Desktop Accountant {desktopVersion}!',
                subtitleUS: 'We have successfully verified your student credentials and your free 5-month trial of QuickBooks Desktop Accountant {desktopVersion} is ready!',
                accessLink: 'To access your free QuickBooks Desktop Accountant {desktopVersion} software, <a class="sid-link" href="{accessLink}" target="_blank">please click here to download a .exe file</a>.',
                accessLinkUS: "To access your free QuickBooks Desktop Accountant {desktopVersion} software, <a class='sid-link' href='{accessLink}' target='_blank'>please click here to download the install file.</a>",
                bestWishes: 'Best wishes on your learning journey with QuickBooks!',
                bestWishesUS: "If you have any questions, please visit our <a href='https://www.intuit.com/partners/education-program/support/' class='sid-link'>support page</a>.",
            },
            tax: {
                title: 'Welcome to Intuit ProFile',
                titleUS: 'Welcome to Intuit ProConnect',
                subtitle: 'We are pleased to provide you with complimentary access to ProFile tax software.',
                subtitleUS: 'We are pleased to provide you with a complimentary trial of ProConnect Tax Online, the #1 cloud-based tax preparation software.',
                accessLink: '<a class="sid-link" href="{accessLink}">Create your account</a>',
                downloadLinkInstructionsCA: 'You will receive your download instructions and license key within 3 business days.',
                bestWishes: 'Best wishes on your journey with ProFile! If you have any questions, <a href="mailto:iepcanada@intuit.com">Education Support</a> can help.',
                bestWishesUS: 'Best wishes on your learning journey with ProConnect!',
                createAccountUS: '<a class="sid-link" href="{accessLink}">Create your account</a>',
                createAccountCA: 'You will receive your download instructions and license key within 3 business days.',
                downloadLinkUS: '{rewardCode}',
                downloadLinkCA: 'https://profile.intuit.ca/download/',
            },
        },
    },
    infoShared: 'Information entered into this web form will be used for verification purposes. The information will be shared with Intuit. All use of Intuit products must comply with United States export control and economic sanction laws. <br /><br /> Intuit will use the personal information you provide to set up and manage your trial, and to communicate important information about your service. Click <a class="sid-link" href="https://security.intuit.com/index.php/privacy" target="_blank">here</a> to view our full Privacy Statement',
};

